import { useEffect, useRef, useState } from "react";
import { Loader2, Printer, Save, Scale } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/common";
import { LabelSheet } from "@/components/stock/LabelSheet";
import { ScaleLabel } from "@/components/stock/ScaleLabel";
import { useAuth } from "@/context/AuthContext";
import { api, brl, canDo, errMsg } from "@/lib/api";
import { encodeScale, scaleCfg } from "@/lib/barcode";

const SIZES = [[40, 30], [50, 30], [60, 40], [80, 50], [100, 50]];
const LAYOUTS = [[4, "2 + código (4) + 0 + valor (6)"], [5, "2 + código (5) + valor (6)"], [6, "2 + código (6) + valor (5)"]];
const sel = "h-10 w-full rounded-md border bg-transparent px-2 text-sm";
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
const addDays = (d, n) => { const x = new Date(`${d}T12:00:00`); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };
const kg = (v) => Number(String(v).replace(",", ".")) || 0;
const codeValue = (cfg, w, total) => (cfg.value === "weight" ? w * 1000 : total * 100);

function FormatBox({ cfg, setCfg, onSave, saving }) {
  const pick = (k, cast = Number) => (e) => setCfg({ ...cfg, [k]: cast(e.target.value) });
  const onSize = (e) => { const [width, height] = e.target.value.split("x").map(Number); setCfg({ ...cfg, width, height }); };
  return (
    <div className="grid gap-3 rounded-lg border p-3 sm:grid-cols-3" data-testid="scale-format">
      <Field label="Padrão do código"><select value={cfg.code_len} onChange={pick("code_len")} className={sel} data-testid="scale-code-len-select">{LAYOUTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
      <Field label="Valor no código"><select value={cfg.value} onChange={pick("value", String)} className={sel} data-testid="scale-value-select"><option value="price">Preço total</option><option value="weight">Peso (gramas)</option></select></Field>
      <Field label="Tamanho da etiqueta"><select value={`${cfg.width}x${cfg.height}`} onChange={onSize} className={sel} data-testid="scale-size-select">{SIZES.map(([w, h]) => <option key={`${w}x${h}`} value={`${w}x${h}`}>{w} x {h} mm</option>)}</select></Field>
      <p className="text-xs text-muted-foreground sm:col-span-2">Se a loja tem balança que imprime etiqueta, use o mesmo padrão dela: o caixa lê as etiquetas das duas.</p>
      <Button variant="outline" size="sm" onClick={onSave} disabled={saving} data-testid="scale-save-format-btn"><Save className="mr-1 h-4 w-4" />Salvar padrão da loja</Button>
    </div>
  );
}

function Preview({ label, cfg, store, hasCode }) {
  if (!label) return <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="scale-preview-empty">Digite o peso para ver a etiqueta.</p>;
  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="overflow-hidden rounded-lg border bg-white p-2" data-testid="scale-label-preview"><div style={{ zoom: Math.min(1.5, 260 / (cfg.width * 3.78)) }}><ScaleLabel p={label} cfg={cfg} store={store} /></div></div>
      <div className="space-y-1 text-sm">
        <p>Total: <b data-testid="scale-total">{brl(label.total)}</b></p>
        <p className="font-mono text-xs text-muted-foreground" data-testid="scale-code-preview">{label.code || (hasCode ? "Valor grande demais para o padrão" : "Código de balança gerado ao imprimir")}</p>
      </div>
    </div>
  );
}

function usePrint(printing, setPrinting, after) {
  useEffect(() => {
    if (!printing) return undefined;
    const t = setTimeout(() => { window.print(); setPrinting(null); after(); }, 400);
    return () => clearTimeout(t);
  }, [printing, setPrinting, after]);
}

export function ScaleLabelDialog({ products, onClose }) {
  const { session, setSession } = useAuth();
  const store = session.tenant.nome_fantasia;
  const saved = scaleCfg(session.tenant.settings);
  const [cfg, setCfg] = useState(saved);
  const [list, setList] = useState(products);
  const [pid, setPid] = useState(products[0]?.id || "");
  const [f, setF] = useState({ weight: "", copies: "1", packed: today(), days: "" });
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(null);
  const wRef = useRef(null);
  const afterPrint = useRef(() => { setF((x) => ({ ...x, weight: "" })); wRef.current?.focus(); }).current;
  usePrint(printing, setPrinting, afterPrint);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const p = list.find((x) => x.id === pid);
  const w = kg(f.weight);
  const total = p ? Math.round(Number(p.price) * w * 100) / 100 : 0;
  const label = p && w > 0 ? { name: p.name, price: p.price, weight: w, total, packed: f.packed, expiry: f.days ? addDays(f.packed, Number(f.days)) : "", code: p.scale_code ? encodeScale(p.scale_code, codeValue(cfg, w, total), cfg) || "" : "" } : null;

  const saveCfg = async (quiet) => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/scale-label", cfg);
      setSession((s) => ({ ...s, tenant: { ...s.tenant, settings: { ...s.tenant.settings, scale_label: data.scale_label } } }));
      if (!quiet) toast.success("Padrão da etiqueta de balança salvo");
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  const print = async () => {
    if (!p || w <= 0) return toast.error("Escolha o produto e digite o peso");
    if (!(Number(p.price) > 0)) return toast.error("Cadastre o preço do kg deste produto");
    const n = Math.floor(Number(f.copies) || 0);
    if (n < 1 || n > 100) return toast.error("Imprima de 1 a 100 etiquetas por vez");
    if (!p.scale_code && !canDo(session.user, "edit_products")) return toast.error("Este produto ainda não tem código de balança. Peça para quem edita produtos cadastrar.");
    setBusy(true);
    try {
      if (JSON.stringify(cfg) !== JSON.stringify(saved)) await saveCfg(true);
      const code = p.scale_code || (await api.post(`/products/${p.id}/scale-code`)).data.scale_code;
      if (!p.scale_code) setList((l) => l.map((x) => (x.id === p.id ? { ...x, scale_code: code } : x)));
      const bar = encodeScale(code, codeValue(cfg, w, total), cfg);
      if (!bar) toast.error(`Não cabe no padrão escolhido: código de balança ${code} ou valor grande demais`);
      else setPrinting(Array.from({ length: n }, () => ({ ...label, code: bar })));
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="scale-label-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Scale className="h-5 w-5 text-primary" />Etiqueta de balança</DialogTitle></DialogHeader>
        {!list.length ? <p className="py-6 text-center text-sm text-muted-foreground" data-testid="scale-no-products">Nenhum produto vendido por quilo. No cadastro do produto, use a unidade Quilo (kg).</p> : (
          <div className="space-y-4">
            <FormatBox cfg={cfg} setCfg={setCfg} onSave={() => saveCfg(false)} saving={saving} />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Produto vendido por kg" className="sm:col-span-2">
                <select value={pid} onChange={(e) => setPid(e.target.value)} className={sel} data-testid="scale-product-select">
                  {list.map((x) => <option key={x.id} value={x.id}>{x.name} · {brl(x.price)}/kg{x.scale_code ? ` · cód. balança ${x.scale_code}` : ""}</option>)}
                </select>
              </Field>
              <Field label="Peso (kg)" hint="Ex.: 0,532 · Enter imprime"><Input ref={wRef} autoFocus value={f.weight} onChange={set("weight")} onKeyDown={(e) => { if (e.key === "Enter") print(); }} inputMode="decimal" placeholder="0,000" data-testid="scale-weight-input" /></Field>
              <Field label="Cópias"><Input type="number" min="1" max="100" value={f.copies} onChange={set("copies")} data-testid="scale-copies-input" /></Field>
              <Field label="Embalado em"><Input type="date" value={f.packed} onChange={set("packed")} data-testid="scale-packed-input" /></Field>
              <Field label="Validade (dias após embalar)" hint="Vazio = não sai na etiqueta"><Input type="number" min="0" value={f.days} onChange={set("days")} data-testid="scale-days-input" /></Field>
            </div>
            <Preview label={label} cfg={cfg} store={store} hasCode={!!p?.scale_code} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="scale-close-btn">Fechar</Button>
          <Button onClick={print} disabled={busy || !list.length} data-testid="scale-print-btn">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Printer className="mr-1 h-4 w-4" />}Imprimir etiqueta</Button>
        </DialogFooter>
        {printing && <LabelSheet labels={printing} cfg={cfg} store={store} Item={ScaleLabel} />}
      </DialogContent>
    </Dialog>
  );
}
